import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewEncapsulation,
    inject,
    input,
    model,
    output,
    signal,
} from '@angular/core';
import { EventBusService } from 'apps/account/src/account-shared/services/event-bus.service';
import { ParseService } from 'apps/account/src/account-shared/services/parse.service';
import { CampaignV1ApiService } from 'apps/account/src/account-shared/services/campaign-v1-api.service';
import { LegacyShimsModule } from '../../../../account-shared/legacy-shims/legacy-shims.module';
import { FtModalComponent } from '../../../common/components/modal/ft-modal.component';
import { FtEnsureModalSingleScrollDirective } from '../../stories/story-modal/ensure-modal-single-scroll.directive';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FtCampaignPrestayComponent } from './ft-campaign-prestay.component';
import { FtCampaignGoalsOnlyComponent } from './ft-campaign-goals-only.component';

/**
 * Native port of the AngularJS `ftCampaignContainer` — the modal chrome (shell, header,
 * activate toggle, `.ft-campaign-master` body host) for the campaign-variation editor.
 * A click-launched, body-mounted modal that routes to the native pre-stay or goals-only
 * editor via `nativeBranch()`. Stays standalone: mounted via createComponent by the launcher.
 */
@Component({
    selector: 'ft-campaign-container-ng',
    imports: [
        LegacyShimsModule,
        FtModalComponent,
        FtEnsureModalSingleScrollDirective,
        TranslateModule,
        FtCampaignPrestayComponent,
        FtCampaignGoalsOnlyComponent,
    ],
    encapsulation: ViewEncapsulation.None,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrls: ['./ft-campaign-container.component.scss'],
    templateUrl: './ft-campaign-container.component.html'
})
export class FtCampaignContainerComponent implements OnInit, OnChanges, OnDestroy {
    readonly propertyUuid = input.required<string>();
    readonly campaignUuid = model.required<string>();
    readonly layerName = input.required<string>();
    readonly isCreating = model(false);

    /**
     * Close signal — emitted by the `.ft-btn-close` button and the create-cancel
     * callback. The launcher (NativeCampaignContainerService) subscribes and tears the
     * mounted instance down (no component→service back-reference). See #3.
     */
    readonly close = output<void>();

    /** Mirrors the v1 `$ftCHC` header model (populated after Campaign.get). */
    protected readonly chc = signal<any>({});

    private _initialized = false;
    private deregChanged?: () => void;

    private readonly Campaign = inject(CampaignV1ApiService);
    private readonly parse: any = inject(ParseService);
    private readonly campaignTypeSvc: any = { None: 0, Custom: 30, AllGuests: 255 }; // v1 .constant('CampaignType')
    private readonly translateSvc = inject(TranslateService);
    private readonly eventBus = inject(EventBusService);
    private readonly zone = inject(NgZone);

    ngOnInit(): void {
        this._initialized = true;
        this.initialize();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this._initialized) return;
        if (changes['propertyUuid'] || changes['campaignUuid'] || changes['layerName'] || changes['isCreating']) {
            this.initialize();
        }
    }

    ngOnDestroy(): void {
        this.deregChanged?.();
    }

    private initialize(): void {
        this.chc.set({});
        this.deregChanged?.();
        this.deregChanged = undefined;
        this.loadHeader();
    }

    /** Port of the inline `$ftCampaignHeaderController` (campaignContainer.component.ts:57-101). */
    private loadHeader(): void {
        if (this.isCreating()) {
            this.chc.set({ isCreating: true });
            return;
        }
        Promise.resolve(
            this.Campaign.get({ propertyUuid: this.propertyUuid(), id: this.campaignUuid(), layerName: this.layerName().replace('-', '') }),
        ).then((response: any) => this.zone.run(() => {
            this.chc.set({
                campaign: response,
                isActive: this.parse.bool(response.isActive),
                campaignType: null,
            });
            // WS5 #10 inc 2i: 'campaign.changed' is native↔native (emitters = the native
            // prestay/goals-only sheets) → the native event bus, not $rootScope.
            this.deregChanged = this.eventBus.on('campaign.changed', (campaign: any) => {
                this.zone.run(() => { this.chc().campaign = campaign; });
            });
        }));
    }

    protected nativeBranch(): string {
        // pre-stay handles BOTH editing and creation natively (#8a — creation is only ever pre-stay);
        // post-stay + photo-advocacy editing are goals-only (#8b). Every container entry point
        // (journeys/audiences showCampaignDetails + createNew) uses one of these three layers, so the
        // v1 $compile fallback is now unreachable and has been removed (#8b).
        if (this.layerName() === 'pre-stay') return 'prestay';
        return 'goals-only';
    }

    /**
     * WS5 Phase 3 #8a — native equivalent of the v1 adapter's campaignCreatedCallback. After
     * FtCampaignPrestayComponent POSTs Campaign.create it emits (created) with the new campaign; flip to
     * editing it (the prestay instance persists — nativeBranch stays 'prestay' — so its ngOnChanges
     * re-activates in editing mode) and reload the header (now shows the title + activate toggle). The
     * modal stays open, exactly as v1 (no hide).
     */
    protected onCampaignCreated(campaign: any): void {
        this.campaignUuid.set(campaign.id.toUpperCase());
        this.isCreating.set(false);
        this.loadHeader();
    }

    // Native port of the v1 `campaignName` filter (components/campaigns/filters/campaignName.js):
    // type 255 → "All other guests" (or "All visitors" when campaignType==6); else campaign.name.
    // Loose `==` matches v1. Translate keys are dotted (no trailing space) → byte-identical.
    protected campaignTitle(): string {
        const campaign = this.chc().campaign;
        if (!campaign) return '';
        if (this.chc().campaignType == 6 && campaign.type == 255) {
            return this.translateSvc.instant('flipto.components.campaign.AllVisitors');
        }
        return campaign.type == 255 ? this.translateSvc.instant('flipto.components.campaign.AllOtherGuests') : campaign.name;
    }

    /** v1 `$ftCHC.canActivate` — `response.type !== CampaignType.AllGuests`. */
    protected showActivate(): boolean {
        return !!this.chc().campaign && this.chc().campaign.type !== this.campaignTypeSvc.AllGuests;
    }

    protected updateState(): void {
        const model = {
            propertyUuid: this.propertyUuid(),
            id: this.campaignUuid(),
            isActive: this.chc().isActive,
            layerName: this.layerName().replace('-', ''),
        };
        // The journeys and audiences pages listen for this to refetch their campaign lists.
        Promise.resolve(this.Campaign.activate(model)).then(() => this.zone.run(() => this.eventBus.emit('campaign.statusChanged', model)));
    }

    protected closeContainer(): void {
        // WS5 Phase 3 #4: emit to the native launcher (NativeCampaignContainerService),
        // which mounted this component and owns its teardown. (Was
        // `$injector.get('CampaignContainer').hide()` — the AngularJS service that
        // $compile-mounted the component; kept registered but no longer the mounter.)
        this.close.emit();
    }
}
